import { useEffect, useState } from "react";
import { Link, Navigate } from "react-router-dom";
import {
  AlertTriangle, ArrowRight, CalendarClock, ChevronRight, Compass, Flame,
  GraduationCap, ListChecks, PauseCircle, Users,
} from "lucide-react";
import { toast } from "sonner";
import api from "@/lib/api";
import { useAuth } from "@/contexts/AuthContext";
import { formatDate } from "@/lib/dates";

const HEALTH_CHIP = {
  green: "bg-forest-600 text-cream-100",
  yellow: "bg-terracotta-500 text-cream-100",
  red: "bg-terracotta-700 text-cream-100",
  paused: "bg-charcoal-muted text-cream-100",
};

const HEALTH_LABEL = {
  green: "On Cadence",
  yellow: "Slipping",
  red: "Cadence Red",
  paused: "Paused",
};

const KIND_ICON = {
  compass_due: Compass,
  cadence_red: AlertTriangle,
  cadence_slipping: AlertTriangle,
  overdue_action: ListChecks,
  overdue_training: GraduationCap,
  lift_task: Flame,
};

export default function MyTeam() {
  const { user } = useAuth();
  const [data, setData] = useState(null);
  const [forbidden, setForbidden] = useState(false);
  const role = user?.role;

  useEffect(() => {
    if (role === "volunteer" || role === "employee") {
      setForbidden(true);
      return;
    }
    api.get("/team/my-team")
      .then((r) => setData(r.data))
      .catch((err) => {
        if (err?.response?.status === 403) setForbidden(true);
        else toast.error("Couldn't load My Team.");
      });
  }, [role]);

  if (forbidden) return <Navigate to="/" replace />;
  if (!data) {
    return (
      <div className="space-y-6" data-testid="my-team-loading">
        <div className="h-8 w-40 rounded-lg bg-cream-200 animate-pulse" />
        <div className="patria-card p-6">
          <div className="h-4 w-40 rounded bg-cream-200 animate-pulse" />
        </div>
      </div>
    );
  }

  const pausedReports = data.reports.filter((r) => r.cadence_health === "paused");
  const anyBlackout = pausedReports.some((r) => r.cadence_paused_reason === "blackout");

  return (
    <div className="space-y-6" data-testid="my-team-page">
      <div>
        <p className="text-xs uppercase tracking-widest text-terracotta-500 font-medium">
          Where Your Rhythm Lives
        </p>
        <h1 className="text-4xl sm:text-5xl font-serif text-forest-900 mt-1" data-testid="my-team-title">
          My Team
        </h1>
        <p className="mt-2 text-sm text-charcoal-soft max-w-2xl">
          One warm view of everyone reporting to you. See what needs your
          attention next, in one glance.
        </p>
      </div>

      {/* Blackout banner — global window for the campus */}
      {anyBlackout && (
        <section
          className="patria-card p-4 border-l-4 border-forest-600 bg-forest-600/5 flex items-start gap-3"
          data-testid="blackout-banner"
        >
          <PauseCircle className="h-5 w-5 text-forest-700 mt-0.5 shrink-0" strokeWidth={1.5} />
          <div>
            <p className="font-medium text-forest-900">Cadence Blackout Window Active</p>
            <p className="text-xs text-charcoal-soft mt-1">
              1:1 cadence math is paused for teammates on your campus during this
              window. Nudges won&rsquo;t fire. You can still schedule and hold 1:1s any time.
            </p>
          </div>
        </section>
      )}

      {/* Needs Your Attention — ranked strip */}
      {data.attention_strip?.length > 0 && (
        <section
          className="patria-card p-4 border-l-4 border-terracotta-500 bg-terracotta-500/5"
          data-testid="my-team-attention"
        >
          <p className="text-xs uppercase tracking-widest text-terracotta-600 font-medium">
            Needs Your Attention
          </p>
          <ul className="mt-3 divide-y divide-sage-300/50">
            {data.attention_strip.slice(0, 8).map((a, i) => {
              const Icon = KIND_ICON[a.kind] || AlertTriangle;
              return (
                <li
                  key={i}
                  className="py-3 flex flex-col sm:flex-row sm:items-start sm:justify-between gap-2"
                >
                  <div className="flex items-start gap-3 min-w-0">
                    <Icon
                      className="h-4 w-4 text-terracotta-600 mt-0.5 shrink-0"
                      strokeWidth={1.5}
                    />
                    <div className="min-w-0">
                      <p className="text-forest-900 font-medium">{a.report_name}</p>
                      <p className="text-xs text-charcoal-muted">{a.detail}</p>
                    </div>
                  </div>
                  <Link
                    to={a.link}
                    className="patria-btn-ghost text-forest-700 self-start sm:self-auto min-h-[44px]"
                    data-testid={`attention-link-${a.kind}-${i}`}
                  >
                    Resolve <ArrowRight className="h-4 w-4 ml-1" strokeWidth={1.5} />
                  </Link>
                </li>
              );
            })}
          </ul>
        </section>
      )}

      {/* Reports — table on desktop, stacked cards on mobile */}
      <section className="space-y-4" data-testid="my-team-reports">
        {/* Mobile stacked cards */}
        <div className="grid grid-cols-1 gap-3 lg:hidden">
          {data.reports.length === 0 && (
            <div className="patria-card p-6 italic text-charcoal-muted">
              No direct reports yet.
            </div>
          )}
          {data.reports.map((r) => <ReportCard key={r.user_id} r={r} />)}
        </div>
        {/* Desktop table */}
        <div className="hidden lg:block patria-card overflow-x-auto">
          <table className="patria-table w-full text-sm">
            <thead>
              <tr>
                <th>Teammate</th>
                <th>Campus</th>
                <th>Next 1:1</th>
                <th>Cadence</th>
                <th>Open · Overdue</th>
                <th>Training</th>
                <th>LIFT</th>
                <th>COMPASS</th>
                <th>Growth Focus · Goals</th>
                <th></th>
              </tr>
            </thead>
            <tbody>
              {data.reports.length === 0 && (
                <tr>
                  <td colSpan={10} className="italic text-charcoal-muted p-4">
                    No direct reports yet.
                  </td>
                </tr>
              )}
              {data.reports.map((r) => (
                <tr key={r.user_id} data-testid={`my-team-row-${r.user_id}`}>
                  <td>
                    <Link
                      to={r.workspace_link || "/rhythm"}
                      className="text-forest-900 font-medium hover:underline"
                    >
                      {r.name}
                    </Link>
                    <p className="text-xs text-charcoal-muted">{r.job_title || "—"}</p>
                  </td>
                  <td className="text-xs text-charcoal-muted">{r.campus_name || "—"}</td>
                  <td className="text-xs">{r.next_1on1 ? formatDate(r.next_1on1) : "—"}</td>
                  <td>
                    <span
                      className={`inline-flex items-center rounded-full px-2 py-0.5 text-xs font-medium ${HEALTH_CHIP[r.cadence_health]}`}
                    >
                      {r.cadence_health === "paused"
                        ? `Paused · ${(r.cadence_paused_reason || "").replace("_", " ")}`
                        : HEALTH_LABEL[r.cadence_health]}
                      {r.cadence_missed_cycles > 0 &&
                        r.cadence_health !== "paused" &&
                        ` · ${r.cadence_missed_cycles}`}
                    </span>
                  </td>
                  <td className="text-xs">
                    {r.open_actions} ·{" "}
                    <span
                      className={
                        r.overdue_actions > 0
                          ? "text-terracotta-600 font-medium"
                          : ""
                      }
                    >
                      {r.overdue_actions}
                    </span>
                  </td>
                  <td className="text-xs">
                    {r.training_incomplete > 0 ? (
                      <Link
                        to={`/training?assignee=${r.user_id}`}
                        className="text-forest-700 hover:underline"
                      >
                        {r.training_incomplete} incomplete
                      </Link>
                    ) : (
                      <span className="text-charcoal-muted">✓</span>
                    )}
                  </td>
                  <td className="text-xs">
                    {r.lift_stage ? (
                      <Link to="/lift" className="patria-chip-terra">
                        {r.lift_stage.replace("_", " ")}
                      </Link>
                    ) : (
                      <span className="text-charcoal-muted">—</span>
                    )}
                  </td>
                  <td className="text-xs">
                    {r.compass_active ? (
                      <span className="patria-chip-terra">
                        <Compass className="h-3 w-3 mr-1" />
                        {r.compass_status}
                      </span>
                    ) : (
                      <span className="text-charcoal-muted">—</span>
                    )}
                  </td>
                  <td className="text-xs max-w-xs">
                    {r.growth_focus ? (
                      <span className="italic text-forest-900">
                        &ldquo;
                        {r.growth_focus.slice(0, 60)}
                        {r.growth_focus.length > 60 ? "…" : ""}
                        &rdquo;
                      </span>
                    ) : (
                      <span className="text-charcoal-muted">Focus not set</span>
                    )}
                    <span className="ml-1 text-charcoal-muted">
                      · {r.goal_count} goals
                    </span>
                  </td>
                  <td>
                    <Link
                      to={r.workspace_link || "/rhythm"}
                      className="patria-btn-ghost text-forest-700"
                      data-testid={`my-team-open-${r.user_id}`}
                    >
                      Open <ChevronRight className="h-4 w-4" strokeWidth={1.5} />
                    </Link>
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      </section>
    </div>
  );
}

function ReportCard({ r }) {
  return (
    <article
      className="patria-card p-4 space-y-3"
      data-testid={`my-team-card-${r.user_id}`}
    >
      <div className="flex items-start justify-between gap-2">
        <div className="min-w-0">
          <Link
            to={r.workspace_link || "/rhythm"}
            className="font-serif text-xl text-forest-900 hover:underline"
          >
            {r.name}
          </Link>
          <p className="text-xs text-charcoal-muted mt-0.5">
            {r.job_title || "—"} · {r.campus_name || "—"}
          </p>
        </div>
        <span
          className={`inline-flex items-center rounded-full px-2 py-1 text-[10px] font-medium ${HEALTH_CHIP[r.cadence_health]}`}
        >
          {r.cadence_health === "paused"
            ? `Paused · ${(r.cadence_paused_reason || "").replace("_", " ")}`
            : HEALTH_LABEL[r.cadence_health]}
        </span>
      </div>
      <div className="grid grid-cols-2 gap-x-3 gap-y-1 text-xs">
        <span className="text-charcoal-muted">Next 1:1</span>
        <span className="text-forest-900">
          {r.next_1on1 ? formatDate(r.next_1on1) : "—"}
        </span>
        <span className="text-charcoal-muted">Open · Overdue</span>
        <span className="text-forest-900">
          {r.open_actions} ·{" "}
          <span
            className={r.overdue_actions > 0 ? "text-terracotta-600 font-medium" : ""}
          >
            {r.overdue_actions}
          </span>
        </span>
        <span className="text-charcoal-muted">Training</span>
        <span className="text-forest-900">
          {r.training_incomplete > 0
            ? `${r.training_incomplete} incomplete`
            : "✓ Done"}
        </span>
        {r.lift_stage && (
          <>
            <span className="text-charcoal-muted">LIFT</span>
            <span className="capitalize text-forest-900">
              {r.lift_stage.replace("_", " ")}
            </span>
          </>
        )}
        {r.compass_active && (
          <>
            <span className="text-charcoal-muted">COMPASS</span>
            <span className="text-forest-900 capitalize">{r.compass_status}</span>
          </>
        )}
      </div>
      {r.growth_focus ? (
        <p className="text-xs italic text-forest-900 border-l-2 border-forest-600 pl-2">
          &ldquo;{r.growth_focus.slice(0, 100)}
          {r.growth_focus.length > 100 ? "…" : ""}&rdquo;
        </p>
      ) : (
        <p className="text-xs text-charcoal-muted italic">Growth Focus not set</p>
      )}
      <Link
        to={r.workspace_link || "/rhythm"}
        className="patria-btn-secondary w-full min-h-[44px]"
        data-testid={`my-team-open-mobile-${r.user_id}`}
      >
        <ChevronRight className="h-4 w-4 mr-1" strokeWidth={1.5} /> Open Workspace
      </Link>
    </article>
  );
}
